import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetService } from "./useGetService";
import {
  API,
  DashboardTabs,
  ETransportationServiceRequestTaskType,
} from "../constants";

// Third party
import _ from "lodash";
import { getUserData } from "../shared/utils";
import {
  EServiceRequestCategory,
  IManualRequest,
  IServiceRequest,
  ITmResponse,
} from "../types/global.types";
import { useArrivalDepartureData } from "./useArrivalDepartureData";

export const useTransportManualRequest = (
  startDate: Date,
  endDate: Date,
  selectedTab: string,
  subSelectedTab: string
) => {
  const [rows, setRows] = useState([]);
  const [isRowsLoading, setRowsLoading] = useState(false);
  const vendorId = getUserData()["cognito:username"];

  const { response, isLoading, handleSearchApply, refreshingText } =
    useArrivalDepartureData({
      startDate,
      endDate,
      selectedTab,
    });

  // FETCH CREATED MANUAL REQUEST DETAILS
  const {
    response: manualRequestDetails,
    isLoading: ismanualRequestDetailsLoading,
    refetch: refetchManualRequest,
  } = useGetService(
    `${API.VENDOR_SERVICE_REQUEST.CREATE_MANUAL_REQUEST}s?vendorId=${vendorId}`
  );

  // FETCH TRANSPORT ASSIGNMENT
  const {
    response: serviceRequests,
    isLoading: isAssignedDataLoading,
    refetch: refetchAssinged,
  } = useGetService(
    `${API.CARNIVAL_ADMIN.GET_SERVICE_REQUEST_BY_VENDOR_ID}?vendorId=${vendorId}`
  );

  const dataHandling = useCallback(
    (
      resData: ITmResponse[],
      manualRequestDetails?: IManualRequest[],
      assignedData?: IServiceRequest[]
    ) => {
      if (!manualRequestDetails?.length) return [];

      const manualTmIds = manualRequestDetails.map((x) => x.tmId);

      return resData
        .filter((item) => manualTmIds.includes(item.EmpId)) // ✅ only manual request matches
        .map((x) => ({
          ...x,
          id: x.EmpId,
          // assigned info check
          assignedInfo:
            !isAssignedDataLoading &&
            assignedData?.find(
              (v: any) => v.tmId === x.EmpId && subSelectedTab === v.requestType
            ),

          // manual request check
          isManualRequestCreated:
            (!ismanualRequestDetailsLoading &&
              manualRequestDetails?.some((v) => v.tmId === x.EmpId)) ||
            false,
        }));
    },
    []
  );

  useEffect(() => {
    setRowsLoading(
      isLoading || ismanualRequestDetailsLoading || isAssignedDataLoading
    );

    if (
      !isLoading &&
      Array.isArray(response?.data) &&
      !ismanualRequestDetailsLoading &&
      Array.isArray(manualRequestDetails?.data)
    ) {
      const processedData = dataHandling(
        response.data,
        manualRequestDetails.data,
        serviceRequests.data
      );
      setRows(processedData as any);
    }

    // Optional: handle case where response is empty
    if (!isLoading && response?.data?.length === 0) {
      setRows([]); // explicitly clear if no result
    }
  }, [
    response,
    isLoading,
    isAssignedDataLoading,
    serviceRequests,
    ismanualRequestDetailsLoading,
    manualRequestDetails,
    subSelectedTab,
  ]);

  const refetchData = (seltab: string) => {
    if (seltab === DashboardTabs.ARRIVALS) {
      refetchAssinged(
        `${API.CARNIVAL_ADMIN.GET_SERVICE_REQUEST_BY_VENDOR_ID}?vendorId=${vendorId}`
      );
    }
    if (seltab === DashboardTabs.DEPARTURE) {
      refetchAssinged(
        `${API.CARNIVAL_ADMIN.GET_SERVICE_REQUEST_BY_VENDOR_ID}?vendorId=${vendorId}`
      );
    }
  };

  return {
    rows,
    isRowsLoading,
    refetchData,
    handleSearchApply,
    refreshingText,
  };
};
